import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Modal, PhasePipeline, Tooltip } from '../../components';
import MentorSlotBooker from '../../components/MentorSlotBooker';
import { getCurrentUser } from '../../data/session';
import { getMyStartup, StartupDoc } from '../../services/startup-service';
import { usePhases } from '../../contexts/PhaseContext';
import { getCourses, CourseDoc } from '../../services/course-service';
import { getMyEnrollments, EnrollmentDoc } from '../../services/enrollment-service';
import { getQuizAttempts, QuizAttempt } from '../../services/quiz-attempt-service';
import { getPitch, PitchSubmissionDoc } from '../../services/pitch-submission-service';
import { getMyRequests, MentoringRequestDoc } from '../../services/mentoring-request-service';

const SectionLabel = ({ label, count, action }: { label: string; count?: number; action?: React.ReactNode }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2.5">
      <h2 className="font-display text-2xl tracking-wide text-text uppercase">{label}</h2>
      {count !== undefined && (
        <span className="text-xs bg-surface border border-line text-muted px-2 py-0.5 rounded-full font-medium">{count}</span>
      )}
    </div>
    {action}
  </div>
);

const FounderDashboardInner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getCurrentUser();
  const firstName = user.name?.split(' ')[0] || 'Founder';
  const { phases, currentPhase, loading: phasesLoading, isPhaseUnlocked, isPhaseComplete, getPhaseProgress, markPhaseComplete, allPhasesComplete } = usePhases();

  const [startup,         setStartup]         = useState<StartupDoc | null | undefined>(undefined);
  const [profileGateTarget, setProfileGateTarget] = useState<'initial' | 'final' | null>(null);
  const [isLoading,       setIsLoading]        = useState(true);
  const [courses,         setCourses]          = useState<CourseDoc[]>([]);
  const [enrollments,     setEnrollments]      = useState<EnrollmentDoc[]>([]);
  const [quizAttempts,    setQuizAttempts]     = useState<QuizAttempt[]>([]);
  const [completingPhase, setCompletingPhase]  = useState(false);
  const [unlockAnim,      setUnlockAnim]       = useState<string | null>(null);
  const [initialPitch,    setInitialPitch]     = useState<PitchSubmissionDoc | null>(null);
  const [finalPitch,      setFinalPitch]       = useState<PitchSubmissionDoc | null>(null);
  const [mentoringRequest, setMentoringRequest] = useState<MentoringRequestDoc | null | undefined>(undefined);

  useEffect(() => {
    const state = location.state as any;
    if (state?.unlockedPhase !== undefined) {
      setUnlockAnim(state.unlockedPhase);
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    getMyStartup()
      .then(st => setStartup(st))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    getPitch('initial').then(p => setInitialPitch(p)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!allPhasesComplete) return;
    getPitch('final').then(p => setFinalPitch(p)).catch(() => {});
    getMyRequests()
      .then(reqs => {
        const active = reqs.find(r => ['pending', 'accepted', 'scheduled', 'rejected'].includes(r.status));
        setMentoringRequest(active || null);
      })
      .catch(() => setMentoringRequest(null));
  }, [allPhasesComplete]);

  useEffect(() => {
    Promise.all([getCourses(), getMyEnrollments(), getQuizAttempts()])
      .then(([c, e, qa]) => {
        setCourses(c.filter(cr => cr.status === 'published'));
        setEnrollments(e);
        setQuizAttempts(qa);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (unlockAnim === null) return;
    const t = setTimeout(() => setUnlockAnim(null), 3000);
    return () => clearTimeout(t);
  }, [unlockAnim]);

  // Derived maps for progress calculation
  const enrollmentMap = new Map(enrollments.map(e => {
    const cid = typeof (e as any).courseId === 'object' ? (e as any).courseId._id : (e as any).courseId;
    return [cid, e];
  }));

  const itemTypeMap = new Map<string, string>();
  courses.forEach(course =>
    (course.modules || []).forEach(mod =>
      mod.items.forEach(item => { if (item._id) itemTypeMap.set(item._id, item.type); })
    )
  );

  const passedQuizItems = new Set(quizAttempts.filter(a => a.passed).map(a => a.itemId));

  const computeCourseProgress = (courseId: string): number => {
    const course = courses.find(c => c._id === courseId);
    const en = enrollmentMap.get(courseId);
    if (!course || !en) return 0;
    const allItems = (course.modules || []).flatMap(m => m.items);
    if (allItems.length === 0) return 100;
    const done = en.completedItems.filter(iid =>
      itemTypeMap.get(iid) === 'quiz' ? passedQuizItems.has(iid) : true
    ).length;
    return Math.round((done / allItems.length) * 100);
  };

  const getProgress = (phaseId: string): number => {
    const p = getPhaseProgress(phaseId);
    if (!p) return 0;
    if (p.status === 'completed') return 100;
    if (p.status !== 'active') return 0;
    const phase = phases.find(ph => ph.id === phaseId);
    if (!phase?.courseIds?.length) return 0;
    return Math.round(
      phase.courseIds.reduce((sum, cid) => sum + computeCourseProgress(cid), 0) /
      phase.courseIds.length
    );
  };

  const currentPhasePct = currentPhase?.courseIds?.length
    ? Math.round(
        currentPhase.courseIds.reduce((sum, cid) => sum + computeCourseProgress(cid), 0) /
        currentPhase.courseIds.length
      )
    : 0;

  const isPhaseFullyDone = !!currentPhase?.courseIds?.length &&
    currentPhase.courseIds.every(cid => computeCourseProgress(cid) === 100);

  const handleCompletePhase = async () => {
    if (!currentPhase || completingPhase) return;
    setCompletingPhase(true);
    try {
      const currentOrder = phases.find(p => p.id === currentPhase.id)?.order ?? 0;
      const nextPhase = phases.find(p => p.order === currentOrder + 1);
      await markPhaseComplete(currentPhase.id);
      setUnlockAnim(nextPhase?.title ?? null);
    } catch {}
    finally { setCompletingPhase(false); }
  };

  const handlePitchNavigate = (target: 'initial' | 'final') => {
    if (startup === null) {
      setProfileGateTarget(target);
    } else {
      navigate(target === 'initial' ? '/app/pitch/initial' : '/app/pitch/final');
    }
  };

  const completedPhases = phases.filter(p => isPhaseComplete(p.id)).length;

  if (isLoading || phasesLoading) {
    return (
      <div className="section-gap">
        <p className="text-muted">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-10 pb-10">

      {/* Unlock animation overlay */}
      {unlockAnim !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-bg/90 backdrop-blur-sm animate-fade-in"
          onClick={() => setUnlockAnim(null)}
        >
          <div className="text-center animate-fade-in-up">
            <div className="w-24 h-24 rounded-full bg-chart/15 border-2 border-chart flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-chart" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h16.5a1.5 1.5 0 001.5-1.5v-7.5a1.5 1.5 0 00-1.5-1.5H3.75a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5z" />
              </svg>
            </div>
            <p className="font-display text-chart text-lg tracking-widest uppercase mb-2">Congratulations!</p>
            <h2 className="font-display text-4xl text-text tracking-wide mb-1">{unlockAnim} Unlocked</h2>
            <p className="text-muted text-sm mt-2">Click anywhere to continue</p>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pt-2">
        <div>
          <p className="font-display text-xs tracking-display text-chart uppercase mb-1">Founder Dashboard</p>
          <h1 className="font-display text-5xl tracking-wide text-text leading-none">
            Hello, {firstName}.
          </h1>
          <p className="font-serif-display italic text-text text-xl mt-2">
            Here's where you stand in the program.
          </p>
        </div>

        {/* Stat pills */}
        <div className="flex flex-wrap gap-2 sm:pb-1">
          <Tooltip content="Phases you have fully completed">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-chart">{completedPhases}</span>
              <span className="text-muted">Phases Done</span>
            </span>
          </Tooltip>
          <Tooltip content="Your progress through the current phase">
            <span className="flex items-center gap-1.5 bg-shell border border-line rounded-full px-3 py-1.5 text-xs cursor-default">
              <span className="font-bold text-chart">{currentPhasePct}%</span>
              <span className="text-muted">Current Phase</span>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* ── My Journey (Phase Pipeline) ── */}
      <section>
        <SectionLabel label="My Journey" />
        <PhasePipeline
          phases={phases}
          isPhaseUnlocked={isPhaseUnlocked}
          isPhaseComplete={isPhaseComplete}
          getProgress={getProgress}
          currentPhaseId={currentPhase?.id}
          phase0={{ isSubmitted: !!initialPitch?.isSubmitted, onClick: () => handlePitchNavigate('initial') }}
          finalPitch={allPhasesComplete ? {
            onClick: () => handlePitchNavigate('final'),
            isGraded: !!(finalPitch?.reviewStatus && finalPitch.reviewStatus !== 'pending'),
          } : undefined}
        />

        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          <button onClick={() => handlePitchNavigate('initial')} className="text-xs text-warning hover:underline text-left">
            {initialPitch?.isSubmitted ? 'Phase 0: Initial Pitch submitted — view' : 'Phase 0: Initial Pitch available — submit now'}
          </button>
          {allPhasesComplete && (
            <button onClick={() => handlePitchNavigate('final')} className="text-xs text-chart hover:underline text-left">
              {!finalPitch?.isSubmitted
              ? 'Final Pitch available — submit now'
              : (finalPitch.reviewStatus && finalPitch.reviewStatus !== 'pending')
                ? 'Final Pitch graded — view'
                : 'Final Pitch submitted — view'}
            </button>
          )}
        </div>
      </section>

      {/* ── Mentoring Session Booker ── */}
      {allPhasesComplete && finalPitch?.isSubmitted && mentoringRequest !== undefined && (
        <section>
          <SectionLabel label="Mentoring Session" />
          <MentorSlotBooker
            existingRequest={mentoringRequest}
            onBooked={() => {
              getMyRequests()
                .then(reqs => {
                  const active = reqs.find(r => ['pending', 'accepted', 'scheduled'].includes(r.status));
                  setMentoringRequest(active || null);
                })
                .catch(() => {});
            }}
          />
        </section>
      )}

      {/* ── Current Phase card ── */}
      {currentPhase && (
        <section>
          <SectionLabel label="Current Phase" />
          <div className="bg-shell border border-line rounded-card p-5 animate-fade-in-up">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <p className="font-display text-micro tracking-widest text-chart uppercase mb-1">In Progress</p>
                <h3 className="font-semibold text-text text-base">{currentPhase.title}</h3>
                {currentPhase.description && (
                  <p className="text-muted text-sm mt-1 leading-relaxed">{currentPhase.description}</p>
                )}
              </div>
              {isPhaseFullyDone ? (
                <Tooltip content="All courses complete — mark this phase done" position="left">
                  <Button variant="primary" size="sm" onClick={handleCompletePhase} disabled={completingPhase}>
                    {completingPhase ? 'Verifying…' : 'Complete Phase'}
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip content="Continue working on this phase" position="left">
                  <Button variant="primary" size="sm" onClick={() => navigate('/app/lms/courses')}>
                    Continue
                  </Button>
                </Tooltip>
              )}
            </div>
            <div className="w-full bg-surface rounded-full h-1.5">
              <div className="bg-chart h-1.5 rounded-full transition-all" style={{ width: `${currentPhasePct}%` }} />
            </div>
            <span className="text-xs text-muted mt-1.5 block tabular-nums">{currentPhasePct}% complete</span>
          </div>
        </section>
      )}

      {/* ── Startup Banner ── */}
      {startup === null ? (
        <section>
          <SectionLabel label="My Startup" />
          <div className="bg-shell border border-line rounded-card p-5 flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-text text-sm">Start Your Journey</p>
              <p className="text-muted text-sm mt-0.5">Apply to the program and create your startup profile.</p>
            </div>
            <Tooltip content="Submit your application to join the program" position="left">
              <Button variant="primary" onClick={() => navigate('/app/startup/apply')}>Apply Now</Button>
            </Tooltip>
          </div>
        </section>
      ) : startup ? (
        <section>
          <SectionLabel label="My Startup" />
          <div className="bg-shell border border-line rounded-card p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-chart/10 border border-chart/20 flex items-center justify-center flex-shrink-0">
                <span className="text-chart font-bold text-base leading-none">{startup.name.charAt(0).toUpperCase()}</span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-text text-sm">{startup.name}</h3>
                  <span className="text-badge bg-chart/10 text-chart px-2 py-0.5 rounded-full capitalize">{startup.stage}</span>
                </div>
                {startup.tagline && <p className="text-muted text-xs mt-0.5 truncate">{startup.tagline}</p>}
              </div>
            </div>
            <Tooltip content="View and edit your business profile" position="left">
              <Button variant="ghost" size="sm" onClick={() => navigate('/app/startup')}>View Profile</Button>
            </Tooltip>
          </div>
        </section>
      ) : null}

      {/* ── Business Profile Gate Modal ── */}
      <Modal
        isOpen={profileGateTarget !== null}
        onClose={() => setProfileGateTarget(null)}
        title="Business Profile Required"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted leading-relaxed">
            Create your business profile before submitting a pitch. It only takes a couple of minutes and will pre-fill your pitch form automatically.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-1">
            <button
              onClick={() => setProfileGateTarget(null)}
              className="px-4 py-2 rounded-card border border-line text-sm text-text hover:bg-surface transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => { setProfileGateTarget(null); navigate('/app/startup/apply'); }}
              className="px-4 py-2 rounded-card bg-chart text-black text-sm font-semibold hover:bg-chart/90 transition-colors"
            >
              Create Business Profile
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FounderDashboardInner;
